<template>
  <div>
    <div class="result-hero">
      <i :class="heroIcon"></i>
      <p class="result-headline">{{ headline }}</p>
      <div class="result-summary">
        <div class="result-stat">
          <span class="result-stat-value">{{ tableResult.length }}</span>
          <span class="result-stat-label">Processadas</span>
        </div>
        <div class="result-stat">
          <span class="result-stat-value result-success">{{ successCount }}</span>
          <span class="result-stat-label">Criadas</span>
        </div>
        <div class="result-stat">
          <span class="result-stat-value result-danger">{{ errorCount }}</span>
          <span class="result-stat-label">Com erro</span>
        </div>
      </div>
    </div>

    <el-table v-if="!isValidFirstRow" :data="tableResult" style="width: 100%" :row-class-name="tableRowClassName">
      <el-table-column type="index" label="Linha" width="100"></el-table-column>
      <el-table-column label="Status" width="150">
        <template slot-scope="scope">
          <el-tag size="small" :type="scope.row.status === 'Erro' ? 'danger' : scope.row.status === 'Sucesso' ? 'success' : 'info'">
            {{ scope.row.status }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Resultado" property="result"> </el-table-column>
      <el-table-column label="Nº Nota" width="120">
        <template slot-scope="scope">
          {{ scope.row.data.data.prepost.nf.nfeNumber || "---"}}
        </template>
      </el-table-column>
    </el-table>
    <el-table v-if="isValidFirstRow" :data="tableResult" style="width: 100%" :row-class-name="tableRowClassName">
      <el-table-column label="Linha" width="100">
        <template slot-scope="scope">
          {{ scope.row.data.data.index+1}}
        </template>
      </el-table-column>
      <el-table-column label="Status" width="150">
        <template slot-scope="scope">
          <el-tag size="small" :type="scope.row.status === 'Erro' ? 'danger' : scope.row.status === 'Sucesso' ? 'success' : 'info'">
            {{ scope.row.status }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Resultado" property="result">
        <template slot-scope="scope">
          {{ scope.row.result }}
        </template>
      </el-table-column>
    </el-table>

    <div class="result-footer">
      <el-button v-if="hasErrors" type="danger" plain @click="revalidate()">Revalidar NFE</el-button>
      <el-button type="primary" icon="el-icon-refresh-right" @click="reset()">Nova importação</el-button>
    </div>
  </div>
</template>

<script>
import { PrepostService } from "@/services/customers/prepost.services";

export default {
  name: "ResultingPrespost",
  props: ["records"],
  methods: {
    reset() {
      this.$emit("reset");
    },
    revalidate() {
      this.$emit("revalidate", this.failedNfe);
    },
    tableRowClassName({ row, rowIndex }) {
      if (row.status === "Erro") {
        return "warning-row";
      } else if (row.status === "Sucesso") {
        return "success-row";
      }
      return "";
    },
  },
  computed: {
    successCount() {
      return this.tableResult.filter(r => r.status === "Sucesso").length;
    },
    errorCount() {
      return this.tableResult.filter(r => r.status === "Erro").length;
    },
    hasErrors() {
      return this.errorCount > 0;
    },
    processing() {
      return this.tableResult.some(r => r.status === "Aguardando" || r.status === "Processando");
    },
    headline() {
      if (this.processing) return "Criando as pré-postagens...";
      if (this.errorCount === 0) return "Importação concluída!";
      return `Importação concluída com ${this.errorCount} erro${this.errorCount > 1 ? "s" : ""}`;
    },
    heroIcon() {
      if (this.processing) return "el-icon-loading result-icon-info";
      if (this.errorCount === 0) return "el-icon-circle-check result-icon-success";
      return "el-icon-warning-outline result-icon-warning";
    },
    isValidFirstRow() {
      if (!this.tableResult || !this.tableResult.length) return false;

      const first = this.tableResult[0];

      return (
        first.result &&
        first.data &&
        first.data.value &&
        first.data.data &&
        first.data.data.index !== undefined
      );
    },
  },
  data() {
    return {
      tableResult: [],
      failedNfe: [],
    };
  },
  async mounted() {
    const vm = this;

    for (const record of vm.records) {
      vm.tableResult.push({ status: "Aguardando", result: "", data: record });
    }

    const loading = vm.$loading(vm.$options.loading);

    const sendPrepostData = async (data) => {
      if (data.error) {
        throw new Exception(data.error);
      }
      return await PrepostService.add(
        data.prepost.customer,
        data.prepost.sender,
        data.prepost.delivery,
        data.prepost.contact,
        data.prepost.object,
        data.prepost.complement,
        data.prepost.service,
        data.prepost.cardpost,
        data.prepost.dc,
        data.prepost.nf,
        data.prepost.primary
      );
    };

    for (const record of vm.tableResult) {
      record.status = "Processando";
      try {
        if (record.data.error) {
          record.status = "Erro";
          record.result = record.data.error.error;
          if (record.data.data && record.data.data.prepost && record.data.data.prepost.nf && record.data.data.prepost.nf.nfeNumber) {
            this.failedNfe.push(record.data.data.prepost.nf.nfeNumber);
          }
        } else {
          record.data.data.prepost.sender.cep = String(record.data.data.prepost.sender.cep || "");
          record.data.data.prepost.sender.federalId = String(record.data.data.prepost.sender.federalId || "");
          record.data.data.prepost.sender.number = String(record.data.data.prepost.sender.number || "");
          record.data.data.prepost.sender.phone = String(record.data.data.prepost.sender.phone || "");

          record.data.data.prepost.delivery.cep = String(record.data.data.prepost.delivery.cep || "");
          record.data.data.prepost.delivery.number = String(record.data.data.prepost.delivery.number || "");
          record.data.data.prepost.delivery.phone = String(record.data.data.prepost.delivery.phone || "");
          record.data.data.prepost.delivery.federalId = String(record.data.data.prepost.delivery.federalId || "");

          record.data.data.prepost.contact.phone = String(record.data.data.prepost.contact.phone  || "");
          record.data.data.prepost.contact.federalid = String(record.data.data.prepost.contact.federalid  || "");
          record.data.data.prepost.object.quantity = 1;

          let csvData = null;

          if (
            record &&
            record.data &&
            record.data.data &&
            record.data.data.data
          ) {
            csvData = record.data.data.data;
          }

          if (csvData && csvData.CHAVE_NFE) {
            record.data.data.prepost.nf = {
              nfeValue: String(csvData.VALOR_NFE || "0"),
              nfeNumber: Number(csvData.NUMERO_NFE),
              nfeSerie: Number(csvData.SERIE_NFE),
              nfeKey: String(csvData.CHAVE_NFE || ""),
            };
          }

          const prepost = await sendPrepostData(record.data.data);
          console.log("")
          record.status = "Sucesso";
          record.result = prepost[0].tag;
        }
      } catch (e) {
        record.status = "Erro";
        record.result = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || e;
        if (record.data && record.data.data && record.data.data.prepost && record.data.data.prepost.nf && record.data.data.prepost.nf.nfeNumber) {
          this.failedNfe.push(record.data.data.prepost.nf.nfeNumber);
        }
      }
    }
    loading.close();
  },
};
</script>

<style>
.el-table .warning-row {
  background: oldlace;
}

.el-table .success-row {
  background: #f0f9eb;
}

.result-hero {
  text-align: center;
  padding: 25px 0 15px;
}

.result-hero > i {
  font-size: 44px;
}

.result-icon-success {
  color: #67c23a;
}

.result-icon-warning {
  color: #e6a23c;
}

.result-icon-info {
  color: #909399;
}

.result-headline {
  margin: 10px 0 25px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.result-summary {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
}

.result-stat {
  margin: 0 35px;
}

.result-stat-value {
  display: block;
  font-size: 28px;
  font-weight: 600;
  color: #303133;
}

.result-stat-value.result-success {
  color: #67c23a;
}

.result-stat-value.result-danger {
  color: #f56c6c;
}

.result-stat-label {
  font-size: 12px;
  color: #909399;
  text-transform: uppercase;
}

.result-footer {
  margin-top: 25px;
  text-align: center;
}
</style>